import {Component, computed, effect, Input, OnChanges, signal, SimpleChanges, inject, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, type BatteryType, type EnergyType, type Interval} from './base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {Chart, ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import annotationPlugin from 'chartjs-plugin-annotation';
import {rxResource} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {SunResponse} from '../../../../api/models/sun-response';
import {environment} from '../../../../environments/environment';
import {LocationStateService} from '../../../../common/services/location-state.service';
import { LocationResponse } from '../../../../api/models/location-response';
import {getAverageEnergyStatus, getPlannedEnergyStatus, MeasuredEnergyKey} from './average-energy-response.helper';

@Component({
  selector: 'app-battery-power-day-chart',
  templateUrl: './chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, CommonModule],
})
export class BatteryPowerDayChartComponent extends BaseChartComponent implements OnChanges {
  @Input() batteryType: BatteryType = 'charge'; // Define input with default value
  @Input() set date(value: Date) {
    this._date.set(value);
  }

  get date(): Date {
    return this._date();
  }

  private _date = signal<Date>(new Date());
  @Input() data: AverageEnergyResponse | undefined;
  type = 'battery';
  override interval: Interval = 'day';
  protected override get summaryType(): EnergyType { return 'battery'; }

  // Override unit to % for battery charge charts, kW for power charts
  @Input() override get unit(): string {
    return this.batteryType === 'charge' ? '%' : 'kW';
  }

  override set unit(value: string) {
    // Allow external setting but ignore it since we determine unit based on batteryType
  }

  // Weather resource for sunrise/sunset data - automatically reloads when date changes
  // Using custom loader that directly accesses the current date from the component
  weatherResource = rxResource<SunResponse, any>({
    params: computed(() => ({body: {date: this._date().toISOString()}})),
    defaultValue: environment.showDefault
      ? {
          'sunrise': this._date().toISOString(),
          'sunset': this._date().toISOString(),
        }
      : {'sunrise': undefined, 'sunset': undefined},
    stream: this.sunDataLoader,
  });

  private readonly locationStateService = inject(LocationStateService);

  // React to location changes and update chart scales accordingly
  private readonly locationEffect = effect(() => {
    const currentLocation = this.locationStateService.currentLocation() as | LocationResponse | undefined;
    const powerRange = currentLocation?.displayChart?.batteryPower;
    const chargeRange = currentLocation?.displayChart?.batteryCharge;

    if (this.batteryPowerLineChartOptions.scales?.['y'] && powerRange) {
      const tickStep = this.getSymmetricAxisTickStep(
        Math.max(Math.abs(powerRange.minkWh ?? 0), Math.abs(powerRange.maxkWh ?? 0)),
      );
      this.batteryPowerLineChartOptions.scales['y'].min = powerRange.minkWh ?? undefined;
      this.batteryPowerLineChartOptions.scales['y'].max = powerRange.maxkWh ?? undefined;
      // Keep tick spacing synchronized with battery origin chart.
      (this.batteryPowerLineChartOptions.scales['y'] as any).ticks = {
        ...((this.batteryPowerLineChartOptions.scales['y'] as any).ticks ?? {}),
        stepSize: tickStep,
        precision: 0,
      };
    }

    if (this.batteryChargeLineChartOptions.scales?.['y'] && chargeRange) {
      this.batteryChargeLineChartOptions.scales['y'].min = chargeRange.minkWh ?? undefined;
      this.batteryChargeLineChartOptions.scales['y'].max = chargeRange.maxkWh ?? undefined;
    }

    // Trigger change detection by creating new object references
    this.batteryPowerLineChartOptions = {...this.batteryPowerLineChartOptions};
    this.batteryChargeLineChartOptions = {...this.batteryChargeLineChartOptions};

    // Refresh current chart options and force chart redraw
    this.chartOptions.set(this.getCustomChartOptions());
    if (this.chart) {
      this.chart.update();
    }
  });

  private sunriseSunsetEffect = effect(() => {
    const sun = this.weatherResource.value();
    // Clear any stale annotations first.
    this.removeAnnotations();

    if (sun?.sunrise) {
      this.batteryPowerLineChartOptions = this.addVerticalLine(this.batteryPowerLineChartOptions, new Date(sun.sunrise), 'Sonnenaufgang', 'rgb(128,128,128)');
      this.batteryChargeLineChartOptions = this.addVerticalLine(
        this.batteryChargeLineChartOptions,
        new Date(sun.sunrise),
        'Sonnenaufgang',
        'rgb(128,128,128)',
      );
    }
    if (sun?.sunset) {
      this.batteryPowerLineChartOptions = this.addVerticalLine(
        this.batteryPowerLineChartOptions,
        new Date(sun.sunset),
        'Sonnenuntergang',
        'rgb(128,128,128)',
      );
      this.batteryChargeLineChartOptions = this.addVerticalLine(
        this.batteryChargeLineChartOptions,
        new Date(sun.sunset),
        'Sonnenuntergang',
        'rgb(128,128,128)',
      );
    }

    //additional horizontal lines for battery charge
    switch (this.batteryType) {
      case 'charge':
        // Add default horizontal lines for battery charge
        this.addHorizontalLine(10, ''); //10%
        this.addHorizontalLine(90, ''); //90% (hide labels, michael does not want to see them - only bolder line is fine
        break;
      case 'power':
      default:
        // sunrise/sunset lines handled reactively by effect when weatherResource loads
        break;
    }

    // refresh options signal so chart receives new reference
    this.chartOptions.set(this.getCustomChartOptions());
    // trigger chart to re-render with new options
    if (this.chart) {
      this.chart.update();
    }
  });

  batteryChargeLineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    spanGaps: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom',
        display: true,
      },
      annotation: {
        annotations: {},
      },
    },
    scales: {
      y: {
        stacked: false,
        beginAtZero: true,
        max: 100, // default, will be overridden by locationEffect
        ticks: {
          callback: function (value) {
            return value + '%';
          },
        },
      },
      x: {
        stacked: false,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        ticks: {
          minRotation: this.isMobile() ? 90 : 0,
          maxRotation: this.isMobile() ? 90 : 0,
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  batteryPowerLineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    spanGaps: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom',
        display: true,
      },
      annotation: {
        annotations: {},
      },
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        min: -10, // default, will be overridden by locationEffect
        max: 10, // default, will be overridden by locationEffect
        ticks: {
          stepSize: 1,
          precision: 0,
        },
        border: {
          display: false,
        },
      },
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        ticks: {
          minRotation: this.isMobile() ? 90 : 0,
          maxRotation: this.isMobile() ? 90 : 0,
          autoSkip: true,
          maxTicksLimit: this.isMobile() ? 12 : 24, // Fewer labels on mobile
          padding: this.isMobile() ? 4 : 8, // Less padding on mobile
          // Add mobile-specific font sizing
          font: {
            size: this.isMobile() ? 10 : 12,
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  constructor() {
    super();
    // Register the annotation plugin
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Performance battery chart received change', changes);
    if (changes['data'] || changes['type'] || changes['date'] || changes['interval']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.weatherResource.reload();
    this.initializeChartData();
  }

  private initializeChartData() {
    console.log('initialize Performance Battery Day Chart');
    if (!this.data) {
      return; // Exit if no data is available
    }
    const typeName = (this.type +
      this.batteryType.charAt(0).toUpperCase() +
      this.batteryType.slice(1)) as MeasuredEnergyKey; // Set the type to batteryType

    const measuredStatus = getAverageEnergyStatus(this.data, typeName);
    const dataItems = [measuredStatus];
    const datasets: any[] = [];
    const allLabels: Date[][] = [];

    let index = 1;
    dataItems.forEach((dataItem) => {
      if (!dataItem || !dataItem.deviceTimeValues) return;

      // Add original deviceTimeValues datasets
      Object.entries(dataItem.deviceTimeValues).forEach(([key, timeValues]) => {
        // Ensure timeValues is not empty
        if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
        // Ensure timeValues is complete
        const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date,  'null', dataItem.timeBucket);
        const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({...tv}));

        // Extract labels from this dataset
        const datasetLabels: Date[] = [];
        completedTimeValues.forEach((item) => {
          const date = new Date(item.read ?? '');
          if (!isNaN(date.getTime())) {
            datasetLabels.push(date);
          }
        });
        allLabels.push(datasetLabels);

        // set color and label for dataset
        const color = this.getGreen(index++);
        const legendLabel = dataItem.deviceNames?.[key] || key; // Use device name or key as label
        datasets.push({
          label: legendLabel, // Use device name or key as label
          data: rawTimeValues.map((v) => v.value), // Extract values for the dataset
          borderColor: color,
          backgroundColor: color,
          fill: this.batteryType === 'power',
          stack: this.batteryType === 'power' ? 'batteryPower' : undefined,
          pointRadius: 0, // Hide dots for better readability
          pointHoverRadius: 0,
          // Disable bezier interpolation so each segment is straight
          tension: 0,
          spanGaps: false,
          borderWidth: this.batteryType === 'power' ? 1 : 2, // Thicker line for power chart and increased thickness for charge chart
        });

      });
    });

    // if (this.batteryType === 'charge' && this.batteryChargeLineChartOptions.scales?.['y']) {
    //   const currentMax = Number(this.locationStateService.currentLocation()?.maxBatteryCharge ?? 100);
    //   const datasetCount = Math.max(1, datasets.length);
    //   this.batteryChargeLineChartOptions.scales['y']!.max = currentMax * datasetCount;
    // }
    // Forecast counterpart (plannedBatteryCharge / plannedBatteryPower), drawn in a
    // much lighter green so a day holding both stays readable.
    this.appendPlannedDatasets({datasets, allLabels}, getPlannedEnergyStatus(this.data, typeName), {
      date: this.date,
      interval: this.interval,
      fillMode: 'null',
      timeBucket: measuredStatus?.timeBucket,
      color: (deviceKey, plannedIndex) => this.getGreen(plannedIndex + 1),
      label: (deviceLabel) => this.getPlannedLabel(deviceLabel),
      dataset: () => ({
        fill: this.batteryType === 'power',
        // Own stack so planned is drawn next to, not on top of, the measured series.
        stack: this.batteryType === 'power' ? 'plannedBatteryPower' : undefined,
        tension: 0,
        borderWidth: this.batteryType === 'power' ? 1 : 2,
      }),
    });

    // Process all labels to ensure they match
    const labels = this.processLabels(allLabels);
    this.updateChart(labels, datasets);
  }

  private updateChart(labels: any[], datasets: any[]) {
    this.chartType.set('line');
    this.chartOptions.set(this.getCustomChartOptions());
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private removeAnnotations() {
    if (this.batteryPowerLineChartOptions.plugins?.annotation) {
      this.batteryPowerLineChartOptions.plugins.annotation.annotations = {};
      this.batteryPowerLineChartOptions = {...this.batteryPowerLineChartOptions};
    }
    if (this.batteryChargeLineChartOptions.plugins?.annotation) {
      this.batteryChargeLineChartOptions.plugins.annotation.annotations = {};
      this.batteryChargeLineChartOptions = {...this.batteryChargeLineChartOptions};
    }

    if (this.chart && this.chart.options && this.chart.options.plugins && this.chart.options.plugins.annotation) {
      this.chart.options.plugins.annotation.annotations = {};
    }

    this.chartOptions.set(this.getCustomChartOptions());

    if (this.chart) {
      this.chart.update();
    }
  }

  private addHorizontalLine(value: number, label: string, color = 'lightgrey') {
    const lineId = `horizontalLine_${value}`; // Unique ID for each line
    // @ts-ignore
    this.batteryChargeLineChartOptions.plugins.annotation.annotations[lineId] = {
      type: 'line',
      // Keep threshold guides behind the battery datasets so the green line stays visible.
      drawTime: 'beforeDatasetsDraw',
      yMin: value,
      yMax: value,
      borderColor: color,
      borderWidth: 2,
      label: {
        display: true,
        content: label,
        position: 'bottom',
        backgroundColor: `rgba(${color},0.5)`,
        color: color,
      },
    };
    this.batteryChargeLineChartOptions = {...this.batteryChargeLineChartOptions};
  }

  private getCustomChartOptions() {
    // Re-apply the current-time marker on every rebuild so it survives view switches
    switch (this.batteryType) {
      case 'charge':
        return this.addCurrentTimeLine(this.batteryChargeLineChartOptions, this._date());
      case 'power':
        return this.addCurrentTimeLine(this.batteryPowerLineChartOptions, this._date());
      default:
        return this.addCurrentTimeLine(this.batteryChargeLineChartOptions, this._date());
    }
  }

  protected getGreen(index: number): string {
    const root = document.documentElement;
    const bluePalette = [
      getComputedStyle(root).getPropertyValue('--color-battery-green').trim(), // primary green from CSS variable
      '#71B451', // standard green -> first color should have the main battery color
      '#A8E6A1', // light green --> second battery should have other color
      '#4A9234', // deep green
      '#9CD96B', // fresh/sky green
      '#1F5D25', // dark green
    ];
    return bluePalette[index % bluePalette.length];
  }
}
